import React, { useContext, useMemo, useState } from 'react';

import styled from 'styled-components';

import { getNow } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';

import CustomSelect from '@components/Gui/NwForm/CustomSelect';
import FilterClearButton from '@components/Gui/NwForm/FilterClearButton';

const Wrapper = styled.div`
    .statement-filter-line {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
        width: calc(100% - 8rem);

        &:first-child {
            width: 100%;
        }
    }

    .statement-type-selector {
        flex: 1;
    }

    .statement-year-selector {
        min-width: 8rem;
        .ant-select {
            min-width: 0 !important;
        }
    }
`;

const StatementFilterSelector = ({ onChange, value, subject }) => {
    //const [controlValue, setControlValue] = useState(value);

    const { cachedList } = useContext(ListContext);
    //get all transactions families
    let transactions = [...cachedList.transactionFamilies.items];

    if (subject) {
        switch (subject) {
            case 'model':
                transactions = transactions.filter(
                    (el) =>
                        !['AgFee', 'CommAsMothAg', 'JobCredit', 'Product', 'MothAgComm', 'MothAgCommAdj'].includes(
                            el.Code
                        )
                );
                break;
            case 'agency':
                transactions = transactions.filter(
                    (el) => !['Refund', 'JobRefund', 'Product', 'AgFee'].includes(el.Code)
                );
                break;
            case 'client':
                transactions = transactions.filter(
                    (el) => !['CommAsMothAg', 'MothAgComm', 'MothAgCommAdj', 'Refund'].includes(el.Code)
                );
                break;
            default:
                break;
        }
    }

    const statementTypeOptions = [
        { value: null, label: 'Any Condition' },
        { value: 'any', label: 'At least a statement line' },
        ...transactions.map((type) => ({ value: type.Code, label: `At least a ${type.Name} line` })),
    ];

    const transactionLabelOptions = useMemo(() => {
        if (!value[0] || value[0] === 'any') {
            return [];
        }
        const transactionFamilySelected = transactions.find((type) => type.Code === value[0]);
        return [
            { value: null, label: 'Any Type' },
            ...transactionFamilySelected.TransactionLabels.map((label) => ({ value: label.ID, label: label.Label })),
        ];
    }, [transactions, value]);

    const currentYear = getNow().year();
    //year options: last ten years and next ten years
    const yearOptions = [
        { value: null, label: 'any year' },
        ...Array.from({ length: 20 }, (_, i) => ({
            value: currentYear - 10 + i,
            label: (currentYear - 10 + i).toString(),
        })),
    ];

    const yearOperatorOptions = [
        { value: null, label: 'in' },
        { value: '>', label: 'after' },
        { value: '<', label: 'before' },
    ];

    const handleChangeType = (selectedValue) => {
        if (!selectedValue) {
            onChange([null, null, null]);
        } else {
            if (!value[1]) {
                onChange([selectedValue, currentYear, null, null]);
            } else {
                onChange([selectedValue, value[1], null, null]);
            }
        }
    };

    const handleChangeYear = (selectedValue) => {
        onChange([value[0], selectedValue, value[2], value[3]]);
    };

    const handleChangeTransactionLabel = (selectedValue) => {
        onChange([value[0], value[1], selectedValue, value[3]]);
    };

    const handleChangeYearOperator = (selectedValue) => {
        onChange([value[0], value[1], value[2], selectedValue]);
    };

    const clearFilter = () => {
        onChange([null, null, null, null]);
    };

    return (
        <Wrapper>
            <div className="statement-filter-line">
                <div className="statement-type-selector">
                    <CustomSelect value={value[0]} onChange={handleChangeType} options={statementTypeOptions} />
                </div>
                <div>
                    <CustomSelect value={value[3]} onChange={handleChangeYearOperator} options={yearOperatorOptions} />
                </div>
                <div className="statement-year-selector">
                    <CustomSelect
                        value={value[1]}
                        onChange={handleChangeYear}
                        options={yearOptions}
                        disabled={!value[0]}
                    />
                </div>
                <FilterClearButton onClick={clearFilter} />
            </div>
            {value[0] && transactionLabelOptions.length > 0 && (
                <div className="statement-filter-line">
                    <div className="statement-transaction-label-selector">
                        <CustomSelect
                            value={value[2]}
                            onChange={handleChangeTransactionLabel}
                            options={transactionLabelOptions}
                            allowClear={true}
                        />
                    </div>
                </div>
            )}
        </Wrapper>
    );
};

export default StatementFilterSelector;
